import { FC } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { X } from 'lucide-react';
import Logo from './Logo';
import { useLogo } from '../context/LogoContext';
import InfinityIcon from './InfinityIcon';

interface NavigationProps {
  isMenuOpen: boolean;
  setIsMenuOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

const Navigation: FC<NavigationProps> = ({ isMenuOpen, setIsMenuOpen }) => {
  const location = useLocation();
  const { isAnimating, isSrivatsaView } = useLogo();
  
  // Determine if we're on the homepage
  const isHomePage = location.pathname === '/';

  const isActive = (path: string) => location.pathname === path;

  const navLinks = [
    { path: '/artists', label: 'Artists' },
    { path: '/programme', label: 'Programme' },
    { path: '/works', label: 'Works' },
    { path: '/journal', label: 'Journal' }
  ];

  return (
    <nav className="fixed w-full flex justify-between items-center px-4 py-4 z-50">
      {/* Show logo in header only if:
          1. We're not on the homepage, AND
          2. We're not in the śrivatsa view, AND
          3. We're not currently animating the logo */}
      {(!isHomePage && !isSrivatsaView && !isAnimating) ? (
        <Link to="/" className="z-50" onClick={() => setIsMenuOpen(false)}>
          <Logo className="w-8 h-8 text-black hover:text-gray-700 transition-colors" />
        </Link>
      ) : (
        /* Empty div to maintain layout when logo is not shown */
        <div className="w-8 h-8"></div>
      )}

      {/* Menu Button with Infinity Symbol */}
      <button
        onClick={() => setIsMenuOpen(!isMenuOpen)}
        className="bg-black text-white p-2 rounded-full hover:bg-gray-800 transition-colors"
        aria-label="Toggle navigation menu"
      >
        {isMenuOpen ? <X size={24} /> : <InfinityIcon size={24} />}
      </button>

      {/* Menu Overlay */}
      {isMenuOpen && (
        <div className="fixed inset-0 bg-white z-40">
          {/* Close area - clicking anywhere outside the menu items will close the menu */}
          <div 
            className="absolute inset-0" 
            onClick={() => setIsMenuOpen(false)}
            aria-hidden="true"
          />
          
          <div className="h-screen flex items-center justify-center relative">
            <div className="space-y-6 sm:space-y-8 px-6 py-4 text-center">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`block text-3xl sm:text-4xl font-book-antiqua tracking-wider uppercase ${isActive(link.path) ? 'text-black' : 'text-gray-600'} hover:text-black transition-colors`}
                  onClick={() => setIsMenuOpen(false)}
                >
                  {link.label}
                </Link>
              ))}
              
              {/* Admin link removed for security */}
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navigation;